import { defineStore } from 'pinia'
import { ref } from 'vue'
import { sessionStorage } from '@shared/helpers/storage'
import { loadDoctorPractice } from '@shared/api-endpoints'

export const useDoctorOutreachStore = defineStore(
  'eu/doctorOutreach',
  () => {
    const prescriberRequest = ref(null)
    const selectedPracticeId = ref(null)

    // The practice a renewal deep link pointed at, resolved once at flow entry
    // (see the EU router hooks). Presentational only — the confirmation screen
    // writes selectedPracticeId when the user confirms it.
    /** @type {import('vue').Ref<Object|null>} */
    const resolvedPractice = ref(null)

    const personalInformation = ref({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
    })

    // The lookup this page load attempted. The router hook calls
    // resolvePractice on every navigation, so the failures (which leave
    // resolvedPractice null) have to be remembered too — otherwise a bad id
    // refetches on each route change.
    let requestedPracticeId = null
    let practiceRequest = null

    /**
     * Resolve a doctor practice by id. Reuse the stored practice when it
     * matches. Return null when loading fails.
     * @param {number} id
     * @returns {Promise<Object|null>}
     */
    async function resolvePractice(id) {
      if (resolvedPractice.value?.id === id) return resolvedPractice.value

      if (requestedPracticeId !== id) {
        requestedPracticeId = id
        practiceRequest = loadDoctorPractice(id)
          .then(({ data }) => data)
          .catch(() => null)
      }

      resolvedPractice.value = await practiceRequest
      return resolvedPractice.value
    }

    return {
      prescriberRequest,
      selectedPracticeId,
      resolvedPractice,
      personalInformation,
      resolvePractice,
    }
  },
  {
    persist: {
      storage: sessionStorage,
    },
  },
)

export default useDoctorOutreachStore
